import { useEffect, useMemo, useRef, useState } from 'react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { Row, SortingState, ColumnOrderState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DndContextProps,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
    TABLE,
    TABLE_CONTAINER,
    TABLE_MOBILE_MEDIA_QUERY,
    TABLE_SCROLL,
    TR,
    TD,
    TD_PINNED_LEFT,
    LOADING_BOX,
    EMPTY_BOX,
} from '../vacationTable.styles';
import { VacationDpCard } from './components/VacationDpCard';
import { VacationDpTableHead } from './components/VacationDpTableHead';
import { VacationDpColumnMenu } from './components/VacationDpColumnMenu';
import { buildVacationDpColumns } from './vacationDpTable.columns';
import {
    EMPTY_DEFAULT,
    LOADING_MESSAGE,
    ROW_ESTIMATE_PX,
    ROW_OVERSCAN,
    SCROLL_MAX_HEIGHT,
    SCROLL_REGION_LABEL,
    SORT_HINT,
    VIRTUALIZE_THRESHOLD,
    resolveColumnProfile,
} from './vacationDpTable.definitions';
import type { DpColumnId } from './vacationDpTable.definitions';
import { LOCKED_COLUMNS } from './vacationDpTable.definitions';
import { useDpColumnPreferences } from './hooks/useDpColumnPreferences';
import type { VacationDpRow } from '../../models/vacationDpBoard.types';
import type { VacationDpTableProps } from './vacationDpTable.types';

function alignmentClass(align?: 'left' | 'right'): string {
    return align === 'right' ? 'text-right' : 'text-left';
}

export function VacationDpTable({
    rows,
    onOpenRequest,
    onOpenHistory,
    isLoading = false,
    emptyMessage = EMPTY_DEFAULT,
    today,
    activeFilter,
}: VacationDpTableProps) {
    const [sorting, setSorting] = useState<SortingState>([{ id: 'deadline', desc: false }]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const isMobile = useMediaQuery(TABLE_MOBILE_MEDIA_QUERY);

    const referenceDate = useMemo(() => today ?? new Date(), [today]);
    const profile = useMemo(() => resolveColumnProfile(activeFilter ?? null), [activeFilter]);

    const {
        columnOrder,
        columnVisibility,
        toggleColumn,
        moveColumn,
        resetToDefault,
        isCustomized,
    } = useDpColumnPreferences(profile);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const handleDragEnd: DndContextProps['onDragEnd'] = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            moveColumn(active.id as DpColumnId, over.id as DpColumnId);
        }
    };

    const columns = useMemo(
        () => buildVacationDpColumns(onOpenRequest, referenceDate, profile, onOpenHistory),
        [onOpenRequest, referenceDate, profile, onOpenHistory]
    );

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting, columnVisibility, columnOrder, columnPinning: { left: LOCKED_COLUMNS } },
        onSortingChange: setSorting,
        // Sem isto o id da linha é o índice, e o índice muda de dono a cada reordenação: o React
        // reaproveitaria o <tr> de um colaborador para outro e o cache de alturas do virtualizador
        // aplicaria a medida errada. collaboratorId é único por linha (uma por saldo).
        getRowId: (row) => row.collaboratorId,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const tableRows = table.getRowModel().rows;

    const isVirtualized = !isMobile && tableRows.length > VIRTUALIZE_THRESHOLD;

    const virtualizer = useVirtualizer({
        count: tableRows.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_ESTIMATE_PX,
        overscan: ROW_OVERSCAN,
        enabled: isVirtualized,
        // Mede por colaborador, não por posição: quem tem duas linhas em Vencimento mantém a altura
        // medida depois de reordenar, em vez de herdar a de quem passou a ocupar aquele índice.
        getItemKey: (index) => tableRows[index]?.id ?? index,
    });

    // Reordenar com a rolagem no meio deixaria o usuário olhando uma faixa arbitrária da nova ordem.
    // Atribuir scrollTop em vez de chamar scrollTo(): é propriedade, existe também no jsdom.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [sorting]);

    const virtualItems = virtualizer.getVirtualItems();

    // As duas linhas-espaçador reservam a altura do que não está montado, para a barra de rolagem
    // refletir a lista inteira. Alternativa seria posicionar as linhas em absolute, o que quebraria
    // o alinhamento de colunas que o colgroup garante.
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const paddingBottom =
        virtualItems.length > 0
            ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
            : 0;

    const renderedRows: { row: Row<VacationDpRow>; index: number }[] = isVirtualized
        ? virtualItems.map((item) => ({ row: tableRows[item.index], index: item.index }))
        : tableRows.map((row, index) => ({ row, index }));

    if (isLoading) {
        return <p className={LOADING_BOX}>{LOADING_MESSAGE}</p>;
    }

    if (rows.length === 0) {
        return (
            <div className={EMPTY_BOX}>
                <p className="text-sm text-raro-gray-600 font-montserrat">{emptyMessage}</p>
            </div>
        );
    }

    if (isMobile) {
        return (
            <div className="space-y-3" data-tour="dp-table">
                {tableRows.map((row) => (
                    <VacationDpCard
                        key={row.id}
                        row={row.original}
                        onOpenRequest={onOpenRequest}
                        onOpenHistory={onOpenHistory}
                        today={referenceDate}
                        profile={profile}
                    />
                ))}

                <p className="text-xs italic text-raro-gray-500 font-montserrat">{SORT_HINT}</p>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-tour="dp-table">
            <div className="flex justify-end">
                <VacationDpColumnMenu
                    columnOrder={columnOrder}
                    columnVisibility={columnVisibility}
                    onToggleColumn={toggleColumn}
                    onMoveColumn={moveColumn}
                    onResetToDefault={resetToDefault}
                    isCustomized={isCustomized}
                    profile={profile}
                />
            </div>

            <div className={TABLE_CONTAINER}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={columnOrder.filter(id => columnVisibility[id] && !LOCKED_COLUMNS.includes(id))} strategy={horizontalListSortingStrategy}>
                        <div
                            ref={scrollRef}
                            // Rolagem própria precisa de foco para funcionar pelo teclado, e de nome para o
                            // leitor de tela dizer onde o foco caiu. Sem virtualização não há o que rolar
                            // na vertical, então nada disso se aplica.
                            role={isVirtualized ? 'region' : undefined}
                            aria-label={isVirtualized ? SCROLL_REGION_LABEL : undefined}
                            tabIndex={isVirtualized ? 0 : undefined}
                            className={clsx(
                                TABLE_SCROLL,
                                isVirtualized && `overflow-y-auto ${SCROLL_MAX_HEIGHT}`
                            )}
                        >
                            <table className={TABLE}>
                        <colgroup>
                            {table.getVisibleLeafColumns().map((column) => (
                                <col key={column.id} style={{ width: `${column.getSize()}px` }} />
                            ))}
                        </colgroup>

                        <VacationDpTableHead table={table} isSticky={isVirtualized} />

                        <tbody>
                            {paddingTop > 0 && (
                                <tr aria-hidden="true" style={{ height: paddingTop }} />
                            )}

                            {renderedRows.map(({ row, index }) => (
                                <tr
                                    key={row.id}
                                    data-index={index}
                                    ref={isVirtualized ? virtualizer.measureElement : undefined}
                                    className={TR}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const align = (
                                            cell.column.columnDef.meta as
                                                | { align?: 'left' | 'right' }
                                                | undefined
                                        )?.align;
                                        const isPinned = cell.column.getIsPinned() === 'left';
                                        const pinnedLeft = cell.column.getStart('left');

                                        return (
                                            <td
                                                key={cell.id}
                                                className={clsx(TD, alignmentClass(align), isPinned && TD_PINNED_LEFT)}
                                                style={isPinned ? { left: pinnedLeft } : undefined}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}

                            {paddingBottom > 0 && (
                                <tr aria-hidden="true" style={{ height: paddingBottom }} />
                            )}
                        </tbody>
                            </table>
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            <p className="text-xs italic text-raro-gray-500 font-montserrat">{SORT_HINT}</p>
        </div>
    );
}

export default VacationDpTable;
